import { z } from 'zod';
import { roleSchema } from './auth.js';
import { assetDtoSchema } from './assets.js';
import { activePersonSchema } from './activity.js';

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: roleSchema,
  createdAt: z.string(),
  disabled: z.boolean(),
  /** Self-registered and not yet approved. */
  pending: z.boolean(),
  projectCount: z.number().int(),
  /** Last sign-in or editor activity; null if never seen since tracking started. */
  lastActiveAt: z.string().nullable(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;
export const adminUserListSchema = z.object({ users: z.array(adminUserSchema) });

export const updateUserRequestSchema = z
  .object({ role: roleSchema.optional(), disabled: z.boolean().optional(), approved: z.literal(true).optional() })
  .refine((v) => v.role !== undefined || v.disabled !== undefined || v.approved !== undefined, {
    message: 'Keine Änderung angegeben',
  });
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const createUserRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  displayName: z.string().trim().min(1).max(100),
  role: roleSchema.default('USER'),
});
export type CreateUserRequest = z.input<typeof createUserRequestSchema>;

/** The generated password is shown to the admin exactly once and never stored in plain text. */
export const userPasswordResponseSchema = z.object({ user: adminUserSchema, password: z.string() });
export type UserPasswordResponse = z.infer<typeof userPasswordResponseSchema>;

const ownerSchema = z.object({ id: z.string(), email: z.string(), displayName: z.string() });

export const adminProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  revision: z.number().int(),
  schemaVersion: z.number().int(),
  updatedAt: z.string(),
  createdAt: z.string(),
  parentProjectId: z.string().nullable(),
  owner: ownerSchema,
  documentBytes: z.number().int(),
  thumbnailUrl: z.string().nullable(),
  /** People working in the project right now. */
  activeUsers: z.array(activePersonSchema),
});
export type AdminProject = z.infer<typeof adminProjectSchema>;
export const adminProjectListSchema = z.object({ projects: z.array(adminProjectSchema) });

/** Key figures for the admin overview; counted on the server so large installations stay fast. */
export const adminStatsSchema = z.object({
  activeUsers: z.number().int(),
  pendingUsers: z.number().int(),
  projects: z.number().int(),
  models: z.number().int(),
  storageBytes: z.number(),
});
export type AdminStats = z.infer<typeof adminStatsSchema>;

export const adminAssetSchema = assetDtoSchema.extend({ owner: ownerSchema.nullable() });
export type AdminAsset = z.infer<typeof adminAssetSchema>;
export const adminAssetListSchema = z.object({ assets: z.array(adminAssetSchema) });

export const auditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  createdAt: z.string(),
  actor: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  /** Name of the project, user, model or price catalog; null once it is gone for good. */
  targetName: z.string().nullable(),
  /** Display name of the person in `metadata.userId` (e.g. who a project was shared with). */
  userName: z.string().nullable(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export const auditListSchema = z.object({
  entries: z.array(auditEntrySchema),
  /** Pass as `after` to load the next, older page; null on the last page. */
  next: z.string().nullable(),
});

export const renameAssetRequestSchema = z.object({ name: z.string().trim().min(1).max(120) });
